import { useBackendApi } from "@/hooks/fetch";
import type { PlayerDropsApiResp, PlayerDropsApiSuccessResp } from "@shared/otherTypes";
import useSWR from "swr";
import DrilldownCard, { DrilldownCardLoading } from "./DrilldownCard";
import TimelineCard from "./TimelineCard";
import { useState, useMemo } from "react";
import { PageHeader } from "@/components/page-header";
import { Activity, Cpu, Skull, Calendar, WifiOff, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { dateToLocaleDateString, dateToLocaleTimeString, isDateToday } from "@/lib/dateTime";
import { cn } from "@/lib/utils";

export type DrilldownRangeSelectionType = {
    startDate: Date;
    endDate: Date;
} | null;
export type DisplayLodType = 'hour' | 'day';
export type ActiveTabType = 'timeline' | 'environment' | 'diagnostics';

/**
 * Get the query params for the player drops api
 * Modifies the end date to include the whole day/hour depending on the display LOD
 */
const getQueryParams = (rangeState: DrilldownRangeSelectionType, displayLod: DisplayLodType) => {
    if (!rangeState) {
        const detailedDaysAgo = displayLod === 'day' ? 14 : 7;
        return {
            queryKey: 'detailedDaysAgo=' + detailedDaysAgo,
            queryParams: { detailedDaysAgo }
        };
    }

    // Guard against invalid dates (can happen from malformed range state)
    const startTs = rangeState.startDate instanceof Date ? rangeState.startDate.getTime() : NaN;
    const endTs = rangeState.endDate instanceof Date ? rangeState.endDate.getTime() : NaN;
    if (isNaN(startTs) || isNaN(endTs)) {
        const detailedDaysAgo = displayLod === 'day' ? 14 : 7;
        return {
            queryKey: 'detailedDaysAgo=' + detailedDaysAgo,
            queryParams: { detailedDaysAgo }
        };
    }

    const newEndDate = new Date(rangeState.endDate);
    if (displayLod === 'day') {
        newEndDate.setHours(23, 59, 59, 999);
    } else {
        newEndDate.setMinutes(59, 59, 999);
    }
    const detailedWindow = `${rangeState.startDate.toISOString()},${newEndDate.toISOString()}`;
    return {
        queryKey: 'detailedWindow=' + detailedWindow,
        queryParams: { detailedWindow }
    };
}

/**
 * The player drops page
 */
export default function PlayerDropsPage() {
    const [displayLod, setDisplayLod] = useState<DisplayLodType>('hour');
    const [drilldownRange, setDrilldownRange] = useState<DrilldownRangeSelectionType>(null);
    const [activeTab, setActiveTab] = useState<ActiveTabType>('timeline');
    const { queryKey, queryParams } = getQueryParams(drilldownRange, displayLod);

    const playerDropsApi = useBackendApi<PlayerDropsApiResp>({
        method: 'GET',
        path: `/playerDropsData`,
    });
    
    const swrDataApiResp = useSWR(`/playerDropsData?${queryKey}`, async () => {
        const data = await playerDropsApi({ queryParams });
        if (!data) throw new Error('empty_response');
        if ('fail_reason' in data) {
            throw new Error(data.fail_reason as string);
        }
        return data as PlayerDropsApiSuccessResp;
    },{
        revalidateOnFocus: false,
    });

    const displayLodSetter = (lod: DisplayLodType) => {
        setDisplayLod(lod);
        setDrilldownRange(null);
    }

    // Render period indicator dynamically
    const periodTextNode = useMemo(() => {
        if (!swrDataApiResp.data) return null;
        const { windowStart, windowEnd } = swrDataApiResp.data.detailed;
        const windowStartDate = new Date(windowStart);
        const windowEndDate = new Date(windowEnd);
        const showDate = !isDateToday(windowStartDate) || !isDateToday(windowEndDate);

        const windowStartTimeStr = dateToLocaleTimeString(windowStartDate, '2-digit', '2-digit');
        const windowStartDateStr = dateToLocaleDateString(windowStartDate, 'short');
        const windowStartStr = showDate ? `${windowStartTimeStr} - ${windowStartDateStr}` : windowStartTimeStr;
        const windowEndTimeStr = dateToLocaleTimeString(windowEndDate, '2-digit', '2-digit');
        const windowEndDateStr = dateToLocaleDateString(windowEndDate, 'short');
        const windowEndStr = showDate ? `${windowEndTimeStr} - ${windowEndDateStr}` : windowEndTimeStr;

        return (
            <div className={cn(
                "flex items-center gap-1.5 text-xs text-muted-foreground bg-white/5 border border-white/5 rounded-lg px-3 py-1 font-mono transition-all duration-300",
                drilldownRange && 'text-scCyan border-scCyan/20 shadow-[0_0_8px_rgba(0,255,209,0.05)] font-semibold'
            )}>
                <Calendar className="size-3.5 text-scCyan" />
                <span>
                    {windowStartStr} to {windowEndStr}
                </span>
            </div>
        );
    }, [swrDataApiResp.data, drilldownRange]);

    const tabClasses = (tab: ActiveTabType) => cn(
        "flex items-center gap-2 px-4 py-2 text-xs md:text-sm font-semibold rounded-lg border transition-all duration-200 cursor-pointer select-none",
        activeTab === tab 
            ? "bg-scCyan/10 border-scCyan/30 text-scCyan shadow-[0_0_12px_rgba(0,255,209,0.15)]" 
            : "bg-white/5 border-transparent text-muted-foreground hover:bg-white/10 hover:text-white"
    );

    return (
        <div className="w-full min-w-96 flex flex-col gap-6">
            <PageHeader 
                title="Disconnect Diagnostics" 
                icon={<WifiOff className="size-6 text-scCyan" />}
            />

            <div className="w-full bg-[#0d101d]/40 border border-white/5 backdrop-blur-xl rounded-xl shadow-lg relative overflow-hidden flex flex-col">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-scCyan/10 to-transparent" />

                {/* Tab Header Controls */}
                <div className="flex flex-col md:flex-row md:items-center justify-between px-6 py-4 border-b border-white/5 gap-4 bg-[#111424]/20">
                    {/* Left: Tabs */}
                    <div className="flex flex-wrap gap-2">
                        <button 
                            onClick={() => setActiveTab('timeline')}
                            className={tabClasses('timeline')}
                        >
                            <Activity className="size-4" />
                            Disconnect Timeline
                        </button>
                        <button 
                            onClick={() => setActiveTab('environment')}
                            className={tabClasses('environment')}
                        >
                            <Cpu className="size-4" />
                            Environment & Changes
                        </button>
                        <button 
                            onClick={() => setActiveTab('diagnostics')}
                            className={tabClasses('diagnostics')}
                        >
                            <Skull className="size-4" />
                            Crash & Kick Diagnostics
                        </button>
                    </div>

                    {/* Right: Selectors and Range Label */}
                    <div className="flex flex-wrap items-center gap-3 self-start md:self-center">
                        {periodTextNode}
                        
                        {drilldownRange && (
                            <Button
                                variant="ghost"
                                onClick={() => setDrilldownRange(null)}
                                className="h-8 text-xs px-3 text-scCyan hover:text-scCyan hover:bg-scCyan/10 border border-scCyan/20"
                            >
                                <RefreshCw className="size-3 mr-1.5" />
                                Clear Filter
                            </Button>
                        )}

                        <Select defaultValue={displayLod} onValueChange={displayLodSetter}>
                            <SelectTrigger
                                className="w-28 h-8 px-3 text-xs bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-200"
                            >
                                <SelectValue placeholder="Display LOD" />
                            </SelectTrigger>
                            <SelectContent className="px-0 bg-card border-white/10 text-white">
                                <SelectItem value={'day'} className="cursor-pointer hover:bg-white/5">
                                    Days
                                </SelectItem>
                                <SelectItem value={'hour'} className="cursor-pointer hover:bg-white/5">
                                    Hours
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Tab Body */}
                <div className="p-6">
                    {activeTab === 'timeline' && (
                        <TimelineCard
                            isError={!!swrDataApiResp.error}
                            dataTs={swrDataApiResp.data?.ts}
                            summaryData={swrDataApiResp.data?.summary}
                            rangeSelected={drilldownRange}
                            rangeSetter={setDrilldownRange}
                            displayLod={displayLod}
                            setDisplayLod={displayLodSetter}
                        />
                    )}

                    {activeTab === 'environment' && (
                        swrDataApiResp.data && !swrDataApiResp.isValidating ? (
                            <DrilldownCard
                                windowStart={swrDataApiResp.data.detailed.windowStart}
                                windowEnd={swrDataApiResp.data.detailed.windowEnd}
                                windowData={swrDataApiResp.data.detailed.windowData}
                                rangeSelected={drilldownRange}
                                displayLod={displayLod}
                                tabMode="environment"
                            />
                        ) : (
                            <DrilldownCardLoading isError={!!swrDataApiResp.error} tabMode="environment" />
                        )
                    )}

                    {activeTab === 'diagnostics' && (
                        swrDataApiResp.data && !swrDataApiResp.isValidating ? (
                            <DrilldownCard
                                windowStart={swrDataApiResp.data.detailed.windowStart}
                                windowEnd={swrDataApiResp.data.detailed.windowEnd}
                                windowData={swrDataApiResp.data.detailed.windowData}
                                rangeSelected={drilldownRange}
                                displayLod={displayLod}
                                tabMode="diagnostics"
                            />
                        ) : (
                            <DrilldownCardLoading isError={!!swrDataApiResp.error} tabMode="diagnostics" />
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
